import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDriverAuth, useDriverSession, useDriverOffers, useActiveTrip } from '../../lib/driver-hooks';
import { Power, MapPin, Navigation, Check, X, Car, Package, CheckCircle2, LogOut, Loader2, Wifi, WifiOff, Bug, RefreshCw, ChevronDown, ChevronUp, Phone, MessageCircle, Map as MapIcon, Clock, CornerUpLeft, CornerUpRight, ArrowUp } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
import { EmbeddedMap } from '../../components/driver/EmbeddedMap';
import { StaticRouteMap } from '../../components/driver/StaticRouteMap';
import type { RouteStep } from '../../lib/routing';
// ROLLBACK: Full NavigationMap (real-time tracking + instructions) disabled
// import { NavigationMap } from '../../components/driver/NavigationMap'
// ─── Map Error Boundary — catches render crashes, falls back to simple map ───
class MapErrorBoundary extends Component<{
  children: React.ReactNode;
  fallbackLat: number | null;
  fallbackLng: number | null;
}, {
  hasError: boolean;
}> {
  constructor(props: any) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError() {
    return {
      hasError: true
    };
  }
  componentDidCatch(error: Error) {
    console.error('[MAP-BOUNDARY] Crash caught:', error.message);
  }
  render() {
    if (this.state.hasError) {
      return <EmbeddedMap destinationLat={this.props.fallbackLat} destinationLng={this.props.fallbackLng} data-id="element-3661" />;
    }
    return this.props.children;
  }
}
// ─── Alert Hook (vibration + sound placeholder) ──────────────────
function useOfferAlert(offersCount: number) {
  const prevCount = useRef(offersCount);
  useEffect(() => {
    if (offersCount > prevCount.current) {
      // Vibrate if supported (mobile)
      if ('vibrate' in navigator) {
        navigator.vibrate([200, 100, 200, 100, 300]);
      }
      // Sound placeholder — replace with actual audio file URL
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbsGczIj2LwN/Xu2sxHjyKvNzXvnQzITuHuNbTvHg3JDyDstDOuHo7KD5+rMnJtHs+K0B5psPDsHxBLkJ0oL2+rH1EMkVvmre5qX1HNUhqlrG0pX5KOEtmkKuwoYBNPE5hjqWtnIJRQVFcg5+nkoVUQ1Jaf5SYhFdGVVxYdoyPdlRPXFhRaIaGb1VUXVNNY3t+ZVtcYFBJXHNzXl1iZE9EUmtpWl1naktBTGRiVVxsb0s+R11dUFtucUw8Rl');
        audio.volume = 0.8;
        audio.play().catch(() => {});
      } catch {}
    }
    prevCount.current = offersCount;
  }, [offersCount]);
}
// ─── Countdown Timer Component ───────────────────────────────────
function CountdownTimer({
  expiresAt
}: {
  expiresAt: string;
}) {
  const [secondsLeft, setSecondsLeft] = useState(0);
  useEffect(() => {
    const calc = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      return Math.max(0, Math.floor(diff / 1000));
    };
    setSecondsLeft(calc());
    const interval = setInterval(() => setSecondsLeft(calc()), 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);
  const isUrgent = secondsLeft <= 10;
  const minutes = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  if (secondsLeft <= 0) {
    return <span className="text-red-400 font-bold text-lg" data-id="element-3662">Expirada</span>;
  }
  return <div className={`text-center ${isUrgent ? 'animate-pulse' : ''}`} data-id="element-3663">
      <div className={`text-5xl font-black tabular-nums ${isUrgent ? 'text-red-400' : 'text-white'}`} data-id="element-3664">
        {minutes > 0 ? `${minutes}:${secs.toString().padStart(2, '0')}` : `${secs}s`}
      </div>
      <p className="text-xs text-white/40 uppercase tracking-widest mt-1" data-id="element-3665">
        Tiempo restante
      </p>
    </div>;
}
// ─── Debug Panel Component ───────────────────────────────────────
function DriverDebugPanel({
  user,
  driverId,
  authDebug,
  offers,
  debugInfo,
  isOnline,
  onRefetch
}: {
  user: any;
  driverId: number | null;
  authDebug: any;
  offers: any[];
  debugInfo: any;
  isOnline: boolean;
  onRefetch: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return <div className="mx-5 mb-4" data-id="element-3666">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between gap-2 bg-[#1A1A1A] border border-white/10 rounded-xl px-4 py-2.5 text-xs" data-id="element-3667">
        <div className="flex items-center gap-2" data-id="element-3668">
          <Bug className="w-3.5 h-3.5 text-yellow-400" data-id="element-3669" />
          <span className="text-yellow-400 font-bold" data-id="element-3670">DEBUG</span>
          <span className="text-white/40" data-id="element-3671">
            driver={driverId || 'null'} | offers={offers.length} | bypass=
            {debugInfo.allOffersCount}
          </span>
        </div>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-white/30" data-id="element-3672" /> : <ChevronDown className="w-3.5 h-3.5 text-white/30" data-id="element-3673" />}
      </button>

      {expanded && <div className="mt-1 bg-[#1A1A1A] border border-white/10 rounded-xl p-4 space-y-2 text-xs font-mono" data-id="element-3674">
          <div className="text-white/60" data-id="element-3675">
            <span className="text-white/30" data-id="element-3676">auth.uid: </span>
            <span className="text-cyan-400" data-id="element-3677">{authDebug.userId || 'null'}</span>
          </div>
          <div className="text-white/60" data-id="element-3678">
            <span className="text-white/30" data-id="element-3679">driverId: </span>
            <span className="text-cyan-400" data-id="element-3680">{driverId ?? 'null'}</span>
          </div>
          <div className="text-white/60" data-id="element-3681">
            <span className="text-white/30" data-id="element-3682">RPC result: </span>
            <span className="text-cyan-400" data-id="element-3683">
              {JSON.stringify(authDebug.rpcResult)}
            </span>
          </div>
          {authDebug.rpcError && <div className="text-red-400" data-id="element-3684">
              <span className="text-white/30" data-id="element-3685">RPC error: </span>
              {JSON.stringify(authDebug.rpcError)}
            </div>}
          <div className="text-white/60" data-id="element-3686">
            <span className="text-white/30" data-id="element-3687">isOnline: </span>
            <span className={isOnline ? 'text-green-400' : 'text-red-400'} data-id="element-3688">
              {String(isOnline)}
            </span>
          </div>

          <div className="border-t border-white/5 pt-2 mt-2" data-id="element-3689" />

          <div className="text-white/60" data-id="element-3690">
            <span className="text-white/30" data-id="element-3691">pending offers: </span>
            <span className={debugInfo.pendingCount > 0 ? 'text-green-400 font-bold' : 'text-red-400'} data-id="element-3692">
              {debugInfo.pendingCount}
            </span>
          </div>
          <div className="text-white/60" data-id="element-3693">
            <span className="text-white/30" data-id="element-3694">all offers (bypass): </span>
            <span className={debugInfo.allOffersCount > 0 ? 'text-yellow-400' : 'text-red-400'} data-id="element-3695">
              {debugInfo.allOffersCount}
            </span>
          </div>
          <div className="text-white/60" data-id="element-3696">
            <span className="text-white/30" data-id="element-3697">realtime: </span>
            <span className={debugInfo.realtimeActive ? 'text-green-400' : 'text-red-400'} data-id="element-3698">
              {debugInfo.realtimeActive ? 'SUBSCRIBED' : 'NOT SUBSCRIBED'}
            </span>
          </div>
          <div className="text-white/60" data-id="element-3699">
            <span className="text-white/30" data-id="element-3700">last fetch: </span>
            <span className="text-white/50" data-id="element-3701">
              {debugInfo.lastFetchTime || 'never'}
            </span>
          </div>
          {debugInfo.lastError && <div className="text-red-400" data-id="element-3702">
              <span className="text-white/30" data-id="element-3703">error: </span>
              {debugInfo.lastError}
            </div>}

          {/* Bypass offers detail */}
          {debugInfo.bypassOffers.length > 0 && <div className="border-t border-white/5 pt-2 mt-2" data-id="element-3704">
              <p className="text-white/30 mb-1" data-id="element-3705">All offers (no filter):</p>
              {debugInfo.bypassOffers.map((o: any) => {
          const expired = new Date(o.expires_at).getTime() < Date.now();
          const expiresIn = Math.round((new Date(o.expires_at).getTime() - Date.now()) / 1000);
          return <div key={o.id} className="text-white/50 pl-2" data-id="element-3706">
                    #{o.id}: {o.status}
                    {expired ? <span className="text-red-400" data-id="element-3707"> EXPIRED</span> : <span className="text-green-400" data-id="element-3708"> {expiresIn}s left</span>}
                  </div>;
        })}
            </div>}

          {debugInfo.allOffersCount === 0 && <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2 mt-2" data-id="element-3709">
              <p className="text-red-400 font-bold" data-id="element-3710">⚠ RLS BLOCKING</p>
              <p className="text-red-400/70" data-id="element-3711">
                Bypass query returned 0 rows. Run RLS_DELIVERY_OFFERS_FIX.sql in
                Supabase.
              </p>
            </div>}

          <button onClick={onRefetch} className="w-full mt-2 flex items-center justify-center gap-2 bg-white/5 border border-white/10 rounded-lg py-2 text-white/50 hover:text-white/80 active:scale-95 transition-all" data-id="element-3712">
            <RefreshCw className="w-3.5 h-3.5" data-id="element-3713" />
            Force Refetch
          </button>
        </div>}
    </div>;
}
// ─── Helper to extract job display data from real DB columns ─────
function getJobDisplayData(job: any) {
  if (!job) return {
    pickup: 'Restaurante',
    dropoff: 'Cliente',
    fee: 0,
    pickupLat: null,
    pickupLng: null,
    dropoffLat: null,
    dropoffLng: null,
    pickupName: 'Restaurante',
    dropoffName: 'Cliente',
    pickupAddress: '',
    dropoffAddress: '',
    customerPhone: null,
    mapsUrl: null
  };
  return {
    pickup: job.pickup_address_text || job.pickup_address || job.origin_address || job.pickup_location || job.restaurant_name || 'Restaurante',
    dropoff: job.dropoff_address_text || job.dropoff_address || job.destination_address || job.dropoff_location || job.customer_address || 'Cliente',
    fee: job.delivery_fee_estimated || job.delivery_fee || job.fee || job.estimated_fee || 0,
    pickupLat: job.pickup_lat || null,
    pickupLng: job.pickup_lng || null,
    dropoffLat: job.dropoff_lat || null,
    dropoffLng: job.dropoff_lng || null,
    pickupName: job.pickup_name || job.restaurant_name || 'Restaurante',
    dropoffName: job.dropoff_name || job.customer_name || 'Cliente',
    pickupAddress: job.pickup_address_text || job.pickup_address || job.origin_address || '',
    dropoffAddress: job.dropoff_address_text || job.dropoff_address || job.destination_address || '',
    customerPhone: job.customer_phone || job.dropoff_phone || job.phone || null,
    mapsUrl: job.maps_url || job.delivery_maps_url || null
  };
}
// ─── Main App ────────────────────────────────────────────────────
export function DriverAppPage() {
  const navigate = useNavigate();
  const {
    user,
    driverId,
    loading: authLoading,
    resolveError,
    logout,
    authDebug,
    retry: retryAuth
  } = useDriverAuth();
  const {
    isOnline,
    toggleStatus
  } = useDriverSession(driverId);
  const {
    offers,
    acceptOffer,
    rejectOffer,
    debugInfo,
    refetch
  } = useDriverOffers(driverId);
  const {
    activeTrip,
    updateTripStatus,
    clearTrip,
    refetchTrip
  } = useActiveTrip(driverId);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusToggling, setStatusToggling] = useState(false);
  const [tripError, setTripError] = useState<string | null>(null);
  const [deliveryComplete, setDeliveryComplete] = useState<{
    fee: number;
  } | null>(null);
  const [offerError, setOfferError] = useState<string | null>(null);
  // ROLLBACK: Advanced navigation state temporarily disabled
  // const [navigationMode, setNavigationMode] = useState(false)
  const [routeInfo, setRouteInfo] = useState<{
    distance: number;
    duration: number;
    steps: RouteStep[];
  } | null>(null);
  // const [routeError, setRouteError] = useState(null)
  // const [recalculating, setRecalculating] = useState(false)
  // ROLLBACK: Navigation mode auto-activate disabled
  // ROLLBACK: Driver position sync disabled (was using watchPosition)
  // Alert on new offers
  useOfferAlert(offers.length);
  // Auto-dismiss delivery complete toast
  useEffect(() => {
    if (!deliveryComplete) return;
    const timer = setTimeout(() => {
      setDeliveryComplete(null);
    }, 2500);
    return () => clearTimeout(timer);
  }, [deliveryComplete]);
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/driver/login');
    }
  }, [user, authLoading, navigate]);
  if (authLoading) {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-3714">
        <div className="w-10 h-10 border-3 border-[#006847] border-t-transparent rounded-full animate-spin" data-id="element-3715" />
      </div>;
  }
  if (!driverId) {
    // Temporary error — show retryable state, NOT "Sin perfil"
    if (resolveError) {
      return <div className="min-h-screen bg-[#0B0B0B] flex flex-col items-center justify-center p-8 text-center" data-id="element-3716">
          <WifiOff className="w-16 h-16 text-amber-500/40 mb-6" data-id="element-3717" />
          <p className="text-white text-xl font-bold mb-2" data-id="element-3718">Error de conexión</p>
          <p className="text-white/50 mb-8 max-w-xs" data-id="element-3719">{resolveError}</p>
          <button onClick={retryAuth} className="text-[#006847] font-bold text-lg py-3 px-8 border border-[#006847] rounded-xl flex items-center gap-2 mb-4" data-id="element-3720">
            <RefreshCw className="w-5 h-5" data-id="element-3721" />
            Reintentar
          </button>
          <button onClick={logout} className="text-white/30 text-sm py-2 px-6" data-id="element-3722">
            Cerrar sesión
          </button>
        </div>;
    }
    // Definitive — no driver profile found
    return <div className="min-h-screen bg-[#0B0B0B] flex flex-col items-center justify-center p-8 text-center" data-id="element-3723">
        <Car className="w-16 h-16 text-white/20 mb-6" data-id="element-3724" />
        <p className="text-white text-xl font-bold mb-2" data-id="element-3725">
          Sin perfil de conductor
        </p>
        <p className="text-white/50 mb-8" data-id="element-3726">
          No tienes un perfil de conductor asignado a esta cuenta.
        </p>
        <button onClick={logout} className="text-[#006847] font-bold text-lg py-3 px-8 border border-[#006847] rounded-xl" data-id="element-3727">
          Cerrar sesión
        </button>
      </div>;
  }
  const handleLogout = async () => {
    if (isOnline) await toggleStatus(false);
    await logout();
  };
  const handleToggleStatus = async () => {
    setStatusToggling(true);
    await toggleStatus(!isOnline);
    setStatusToggling(false);
  };
  const handleAccept = async (offerId: number) => {
    setActionLoading(true);
    setOfferError(null);
    console.log('[DRIVER-UI] 🚀 Accepting offer:', offerId);
    const result = await acceptOffer(offerId);
    console.log('[DRIVER-UI] Accept result:', result);
    if (!result || result.success === false) {
      const errorMsg = result?.error || 'Error desconocido al aceptar';
      console.error('[DRIVER-UI] ❌ Accept failed:', errorMsg);
      setOfferError(errorMsg);
      setTimeout(() => setActionLoading(false), 1000);
      return;
    }
    console.log('[DRIVER-UI] ✅ Accept succeeded — refreshing state');
    setOfferError(null);
    // Immediately fetch active trip to transition into trip screen
    console.log('[DRIVER-UI] 🔄 Post-accept: fetching active trip...');
    await refetchTrip();
    console.log('[DRIVER-UI] 🔄 Post-accept: active trip fetch complete');
    console.log('[DRIVER-UI] 🔄 Post-accept: activeTrip will update on next render');
    setActionLoading(false);
  };
  const handleReject = async (offerId: number) => {
    setActionLoading(true);
    await rejectOffer(offerId);
    setActionLoading(false);
  };
  // ROLLBACK: Route callbacks disabled (NavigationMap not rendered)
  // const handleRouteError = useCallback((error: string) => { ... }, [])
  // const handleRouteUpdate = useCallback((data) => { ... }, [])
  // const handleRetryRoute = () => { ... }
  const handleStatusChange = async (action: 'arrived' | 'picked_up' | 'en_route' | 'delivered', jobId: number) => {
    console.log('[DRIVER-UI] ═══════════════════════════════════');
    console.log('[DRIVER-UI] Button clicked:', action);
    console.log('[DRIVER-UI] Job ID:', jobId);
    console.log('[DRIVER-UI] Current activeTrip status:', activeTrip?.status);
    console.log('[DRIVER-UI] Order ID on trip:', activeTrip?.order_id);
    // Capture fee before clearing trip
    const currentFee = activeTrip ? activeTrip.delivery_fee_estimated || activeTrip.delivery_fee || activeTrip.fee || 0 : 0;
    const orderId = activeTrip?.order_id;
    setActionLoading(true);
    setTripError(null);
    // Step 1: Update delivery_jobs via hook (RPC + fallback)
    const result = await updateTripStatus(action, jobId);
    console.log('[DRIVER-UI] updateTripStatus result:', result);
    if (result && !result.success) {
      console.error('[DRIVER-UI] ❌ Status change failed:', result.error);
      const errorMsg = action === 'picked_up' ? 'No se pudo actualizar el pedido. Intenta de nuevo.' : action === 'delivered' ? 'No se pudo marcar como entregado. Intenta de nuevo.' : result.error || 'Error actualizando estado';
      setTripError(errorMsg);
      setActionLoading(false);
      return;
    }
    // Step 2: DIRECT update to public.orders (belt-and-suspenders)
    // This guarantees the orders table is updated even if syncOrderFromDeliveryJob failed silently
    if (orderId && (action === 'picked_up' || action === 'delivered')) {
      const orderUpdatePayload: Record<string, any> = {};
      if (action === 'picked_up') {
        console.log('[DRIVER-UI] 📝 Updating order to picked_up — order_id:', orderId);
        orderUpdatePayload.status = 'out_for_delivery';
        orderUpdatePayload.picked_up_at = new Date().toISOString();
        if (driverId) {
          orderUpdatePayload.driver_id = driverId;
        }
      } else if (action === 'delivered') {
        console.log('[DRIVER-UI] 📝 Updating order to delivered — order_id:', orderId);
        orderUpdatePayload.status = 'delivered';
        orderUpdatePayload.delivered_at = new Date().toISOString();
        if (driverId) {
          orderUpdatePayload.driver_id = driverId;
        }
      }
      console.log('[DRIVER-UI] 📝 Direct orders update payload:', orderUpdatePayload);
      const {
        data: orderUpdateResult,
        error: orderUpdateError
      } = await supabase.from('orders').update(orderUpdatePayload).eq('id', orderId).select('id, status');
      if (orderUpdateError) {
        console.error('[DRIVER-UI] ❌ Supabase update error on public.orders:', orderUpdateError.message);
        console.error('[DRIVER-UI] Error details:', JSON.stringify(orderUpdateError));
        // Don't block the flow — the delivery_jobs update already succeeded
      } else {
        console.log('[DRIVER-UI] ✅ Supabase update success on public.orders:', JSON.stringify(orderUpdateResult));
      }
    } else if (!orderId) {
      console.warn('[DRIVER-UI] ⚠️ No order_id on activeTrip — cannot update public.orders directly');
    }
    // Chain picked_up -> en_route automatically to simplify UX
    if (action === 'picked_up' && result?.success) {
      console.log('[DRIVER-UI] 🔗 Chaining picked_up -> en_route');
      const chainResult = await updateTripStatus('en_route', jobId);
      if (chainResult && !chainResult.success) {
        console.warn('[DRIVER-UI] ⚠️ Chained en_route failed, but picked_up succeeded:', chainResult.error);
      }
      // ROLLBACK: Navigation mode activation disabled
      console.log('[DRIVER-UI] 🗺️ Picked up — step 3 active');
    }
    // On successful delivery: haptic feedback + show success
    if (action === 'delivered' && result?.success) {
      // Haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate([100]);
      }
      console.log('[DRIVER-UI] ✅ Delivery complete — showing success');
      // Clear trip and show success toast
      clearTrip();
      setTripError(null);
      setActionLoading(false);
      setDeliveryComplete({
        fee: currentFee
      });
      // Refresh offers in background so driver is ready for next trip
      refetch();
      return;
    }
    setActionLoading(false);
    console.log('[DRIVER-UI] ═══════════════════════════════════');
  };
  // Priority offer = first pending offer
  const priorityOffer = offers.length > 0 ? offers[0] : null;
  // ─── SCREEN: OFFLINE ──────────────────────────────────────────
  if (!isOnline) {
    return <div className="min-h-screen bg-[#0B0B0B] flex flex-col text-white" data-id="element-3728">
        {/* Mini header */}
        <div className="flex items-center justify-between px-5 py-4" data-id="element-3729">
          <div className="flex items-center gap-2" data-id="element-3730">
            <Car className="w-5 h-5 text-white/30" data-id="element-3731" />
            <span className="font-bold text-white/50 text-sm" data-id="element-3732">
              NOIZA Driver
            </span>
          </div>
          <button onClick={handleLogout} className="p-2 text-white/30" data-id="element-3733">
            <LogOut className="w-5 h-5" data-id="element-3734" />
          </button>
        </div>

        {/* Big offline state */}
        <div className="flex-1 flex flex-col items-center justify-center px-8" data-id="element-3735">
          <div className="w-32 h-32 rounded-full bg-[#1A1A1A] border-4 border-white/10 flex items-center justify-center mb-8" data-id="element-3736">
            <WifiOff className="w-16 h-16 text-white/20" data-id="element-3737" />
          </div>
          <h2 className="text-3xl font-black text-white/30 mb-2" data-id="element-3738">
            DESCONECTADO
          </h2>
          <p className="text-white/30 text-center mb-12" data-id="element-3739">
            No estás recibiendo viajes
          </p>

          <button onClick={handleToggleStatus} disabled={statusToggling} className="w-full max-w-xs py-6 rounded-2xl bg-[#006847] text-white font-black text-2xl flex items-center justify-center gap-3 shadow-2xl shadow-[#006847]/30 active:scale-95 transition-transform disabled:opacity-50" data-id="element-3740">
            {statusToggling ? <Loader2 className="w-8 h-8 animate-spin" data-id="element-3741" /> : <>
                <Power className="w-8 h-8" data-id="element-3742" />
                CONECTAR
              </>}
          </button>
        </div>
      </div>;
  }
  // ─── SCREEN: DELIVERY COMPLETE (brief success) ─────────────────
  if (deliveryComplete) {
    return <div className="min-h-screen bg-[#0B0B0B] flex flex-col items-center justify-center text-white px-8" data-id="element-3743">
        <div className="w-24 h-24 rounded-full bg-green-500/10 flex items-center justify-center mb-6 animate-[scale-in_0.3s_ease-out]" data-id="element-3744">
          <CheckCircle2 className="w-14 h-14 text-green-400" data-id="element-3745" />
        </div>
        <h2 className="text-3xl font-black text-white mb-2" data-id="element-3746">
          ¡Entrega completada!
        </h2>
        {deliveryComplete.fee > 0 && <p className="text-2xl font-bold text-green-400 mb-2" data-id="element-3747">
            {formatCurrency(deliveryComplete.fee, '₡')}
          </p>}
        <p className="text-white/40 text-sm" data-id="element-3748">Volviendo a buscar viajes...</p>
        <div className="mt-6 w-48 h-1 bg-white/10 rounded-full overflow-hidden" data-id="element-3749">
          <div className="h-full bg-green-500 rounded-full animate-[shrink_2.5s_linear_forwards]" data-id="element-3750" />
        </div>
      </div>;
  }
  // ─── SCREEN: ACTIVE TRIP (highest priority) ───────────────────
  if (activeTrip) {
    const tripData = getJobDisplayData(activeTrip);
    let step = 1;
    let progress = 33;
    let title = 'PASO 1 DE 3';
    let headerBadge = '🏪 Vas al restaurante';
    let destinationLabel = 'Dirección del restaurante';
    let destinationName = tripData.pickupName;
    let destinationAddress = tripData.pickupAddress;
    let mapLat = tripData.pickupLat;
    let mapLng = tripData.pickupLng;
    let mapsUrl = tripData.mapsUrl;
    if (activeTrip.status === 'arrived_pickup') {
      step = 2;
      progress = 66;
      title = 'PASO 2 DE 3';
      headerBadge = '📦 Ya llegaste al restaurante';
      destinationLabel = 'Dirección del cliente';
      destinationName = tripData.dropoffName;
      destinationAddress = tripData.dropoffAddress;
      mapLat = tripData.dropoffLat;
      mapLng = tripData.dropoffLng;
    } else if (activeTrip.status === 'picked_up' || activeTrip.status === 'en_route') {
      step = 3;
      progress = 100;
      title = 'PASO 3 DE 3';
      headerBadge = '🚗 Camino al cliente';
      destinationLabel = 'Dirección del cliente';
      destinationName = tripData.dropoffName;
      destinationAddress = tripData.dropoffAddress;
      mapLat = tripData.dropoffLat;
      mapLng = tripData.dropoffLng;
    }
    return <div className="min-h-screen bg-[#0B0B0B] flex flex-col text-white" data-id="element-3751">
        {/* Header */}
        <div className="bg-[#141414] border-b border-white/5 px-5 py-3 flex items-center justify-between" data-id="element-3752">
          <div className="flex flex-col" data-id="element-3753">
            <span className="font-bold text-white/50 text-[10px] tracking-widest mb-0.5" data-id="element-3754">
              {title}
            </span>
            <span className="font-black text-white text-sm" data-id="element-3755">{headerBadge}</span>
          </div>
          <span className="text-2xl font-black text-[#00a870]" data-id="element-3756">
            {formatCurrency(tripData.fee, '₡')}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="h-1.5 w-full bg-[#1A1A1A]" data-id="element-3757">
          <div className="h-full bg-[#00a870] transition-all duration-500 ease-out" style={{
          width: `${progress}%`
        }} data-id="element-3758" />
        </div>

        {/* Error Banner */}
        {tripError && <div className="mx-4 mt-3 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-start gap-3" data-id="element-3759">
            <span className="text-red-400 text-sm font-mono flex-1" data-id="element-3760">
              {tripError}
            </span>
            <button onClick={() => setTripError(null)} className="text-red-400/60 text-xs font-bold" data-id="element-3761">
              ✕
            </button>
          </div>}

        {/* Main Content */}
        {step === 3 ? <div className="flex-1 flex flex-col overflow-y-auto" data-id="element-3762">
            {/* Route map — shows driver + destination + polyline via OSRM */}
            {/* Falls back to simple Google Maps iframe if StaticRouteMap crashes */}
            <div className="px-4 pt-4 h-64" data-id="element-3763">
              {mapLat && mapLng ? <MapErrorBoundary fallbackLat={mapLat} fallbackLng={mapLng} data-id="element-3764">
                  <StaticRouteMap destinationLat={mapLat} destinationLng={mapLng} onRouteData={setRouteInfo} data-id="element-3765" />
                </MapErrorBoundary> : <EmbeddedMap destinationLat={mapLat} destinationLng={mapLng} data-id="element-3766" />}
            </div>

            {/* ETA / Distance Card — only shown when OSRM route data is available */}
            {routeInfo && <div className="px-4 pt-3" data-id="element-3767">
                <div className="bg-[#141414] border border-[#00a870]/20 rounded-2xl p-5 flex items-center justify-around" data-id="element-3768">
                  <div className="flex flex-col items-center" data-id="element-3769">
                    <Clock className="w-6 h-6 text-[#00a870] mb-1.5" data-id="element-3770" />
                    <p className="text-3xl font-black text-white tabular-nums" data-id="element-3771">
                      {Math.max(1, Math.round(routeInfo.duration / 60))}
                    </p>
                    <p className="text-xs text-white/40 font-bold uppercase tracking-wider mt-0.5" data-id="element-3772">
                      min
                    </p>
                  </div>
                  <div className="w-px h-14 bg-white/10" data-id="element-3773" />
                  <div className="flex flex-col items-center" data-id="element-3774">
                    <Navigation className="w-6 h-6 text-[#00a870] mb-1.5" data-id="element-3775" />
                    <p className="text-3xl font-black text-white tabular-nums" data-id="element-3776">
                      {routeInfo.distance >= 1000 ? (routeInfo.distance / 1000).toFixed(1) : Math.round(routeInfo.distance)}
                    </p>
                    <p className="text-xs text-white/40 font-bold uppercase tracking-wider mt-0.5" data-id="element-3777">
                      {routeInfo.distance >= 1000 ? 'km' : 'm'}
                    </p>
                  </div>
                </div>
              </div>}

            {/* Next Instruction Card — only shown when steps are available */}
            {routeInfo && routeInfo.steps.length > 0 && (() => {
          // Find the first meaningful step (skip 'depart' which is just "head towards...")
          const nextStep = routeInfo.steps.find(s => s.type !== 'depart' && s.type !== 'arrive') || routeInfo.steps[0];
          if (!nextStep || nextStep.type === 'arrive') return null;
          const isLeft = nextStep.modifier?.includes('left');
          const isRight = nextStep.modifier?.includes('right');
          const DirectionIcon = isLeft ? CornerUpLeft : isRight ? CornerUpRight : ArrowUp;
          const stepDist = nextStep.distance >= 1000 ? `${(nextStep.distance / 1000).toFixed(1)} km` : `${Math.round(nextStep.distance)} m`;
          return <div className="px-4 pt-3" data-id="element-3778">
                    <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-4 flex items-center gap-4" data-id="element-3779">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${isLeft ? 'bg-blue-500/15' : isRight ? 'bg-blue-500/15' : 'bg-white/5'}`} data-id="element-3780">
                        <DirectionIcon className={`w-8 h-8 ${isLeft || isRight ? 'text-blue-400' : 'text-white/50'}`} data-id="element-3781" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-3782">
                        <p className="text-white text-lg font-black leading-tight truncate" data-id="element-3783">
                          {nextStep.instruction}
                        </p>
                        <p className="text-white/40 text-sm font-bold mt-0.5" data-id="element-3784">
                          en {stepDist}
                        </p>
                      </div>
                    </div>
                  </div>;
        })()}

            {/* Customer Info Card */}
            <div className="px-4 pt-4" data-id="element-3785">
              <div className="bg-[#141414] border border-white/10 rounded-2xl p-5" data-id="element-3786">
                <div className="flex items-center justify-center gap-2 mb-2" data-id="element-3787">
                  <Navigation className="w-4 h-4 text-white/40" data-id="element-3788" />
                  <p className="text-xs text-white/40 uppercase tracking-widest font-bold" data-id="element-3789">
                    {destinationLabel}
                  </p>
                </div>
                <p className="text-2xl font-black leading-tight mb-1 text-center" data-id="element-3790">
                  {destinationName || 'Cliente'}
                </p>
                {destinationAddress && <p className="text-white/50 text-sm leading-relaxed text-center" data-id="element-3791">
                    {destinationAddress}
                  </p>}

                {/* Contact buttons */}
                {tripData.customerPhone && <div className="flex justify-center gap-3 mt-4 pt-3 border-t border-white/5" data-id="element-3792">
                    <a href={`tel:${tripData.customerPhone}`} className="flex items-center gap-2 py-2 px-4 bg-white/5 rounded-xl active:scale-95 transition-transform" data-id="element-3793">
                      <Phone className="w-4 h-4 text-white/60" data-id="element-3794" />
                      <span className="text-white/60 text-sm font-bold" data-id="element-3795">
                        Llamar
                      </span>
                    </a>
                    <a href={`https://wa.me/${tripData.customerPhone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 py-2 px-4 bg-[#25D366]/10 rounded-xl active:scale-95 transition-transform" data-id="element-3796">
                      <MessageCircle className="w-4 h-4 text-[#25D366]" data-id="element-3797" />
                      <span className="text-[#25D366] text-sm font-bold" data-id="element-3798">
                        WhatsApp
                      </span>
                    </a>
                  </div>}
              </div>
            </div>

            {/* Spacer */}
            <div className="flex-1 min-h-4" data-id="element-3799" />

            {/* Actions */}
            <div className="px-4 pb-6 pt-4 space-y-3" data-id="element-3800">
              <button disabled={actionLoading} onClick={() => handleStatusChange('delivered', activeTrip.id)} className="w-full py-7 bg-[#006847] text-white font-black text-2xl rounded-3xl flex justify-center items-center gap-3 active:scale-95 transition-transform disabled:opacity-50 shadow-xl shadow-[#006847]/20" data-id="element-3801">
                {actionLoading ? <Loader2 className="w-8 h-8 animate-spin" data-id="element-3802" /> : <>
                    <CheckCircle2 className="w-8 h-8" data-id="element-3803" /> PEDIDO ENTREGADO
                  </>}
              </button>

              {mapLat && mapLng && <a href={`https://www.google.com/maps/dir/?api=1&destination=${mapLat},${mapLng}&travelmode=driving`} target="_blank" rel="noreferrer" className="w-full py-4 bg-transparent border border-white/10 text-white/50 font-bold text-sm rounded-2xl flex justify-center items-center gap-2 active:scale-95 transition-transform" data-id="element-3804">
                  <MapIcon className="w-4 h-4" data-id="element-3805" /> Abrir en Google Maps
                </a>}
            </div>
          </div> : <div className="flex-1 flex flex-col overflow-y-auto" data-id="element-3806">
            {/* Embedded Map */}
            <div className="px-4 pt-4" data-id="element-3807">
              <EmbeddedMap destinationLat={mapLat} destinationLng={mapLng} data-id="element-3808" />
            </div>

            {/* Destination Card */}
            <div className="px-4 pt-4" data-id="element-3809">
              <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 text-center" data-id="element-3810">
                <div className="flex items-center justify-center gap-2 mb-2" data-id="element-3811">
                  {step === 1 ? <MapPin className="w-4 h-4 text-white/40" data-id="element-3812" /> : <Navigation className="w-4 h-4 text-white/40" data-id="element-3813" />}
                  <p className="text-xs text-white/40 uppercase tracking-widest font-bold" data-id="element-3814">
                    {destinationLabel}
                  </p>
                </div>
                <p className="text-2xl font-black leading-tight mb-1" data-id="element-3815">
                  {destinationName}
                </p>
                {destinationAddress && <p className="text-white/50 text-sm leading-relaxed" data-id="element-3816">
                    {destinationAddress}
                  </p>}
                {step === 2 && <div className="mt-4 pt-3 border-t border-white/5" data-id="element-3817">
                    <p className="text-[#00a870] text-sm font-bold uppercase tracking-wide" data-id="element-3818">
                      Ahora ve a entregar al cliente
                    </p>
                  </div>}
              </div>
            </div>

            {/* Spacer */}
            <div className="flex-1 min-h-4" data-id="element-3819" />

            {/* Actions */}
            <div className="px-4 pb-6 pt-4 space-y-3" data-id="element-3820">
              {step === 1 && <button disabled={actionLoading} onClick={() => handleStatusChange('arrived', activeTrip.id)} className="w-full py-7 bg-white text-black font-black text-2xl rounded-3xl flex justify-center items-center gap-3 active:scale-95 transition-transform disabled:opacity-50" data-id="element-3821">
                  {actionLoading ? <Loader2 className="w-8 h-8 animate-spin" data-id="element-3822" /> : <>
                      <MapPin className="w-8 h-8" data-id="element-3823" /> YA LLEGUÉ
                    </>}
                </button>}

              {step === 2 && <button disabled={actionLoading} onClick={() => handleStatusChange('picked_up', activeTrip.id)} className="w-full py-7 bg-[#006847] text-white font-black text-2xl rounded-3xl flex justify-center items-center gap-3 active:scale-95 transition-transform disabled:opacity-50 shadow-xl shadow-[#006847]/20" data-id="element-3824">
                  {actionLoading ? <>
                      <Loader2 className="w-8 h-8 animate-spin" data-id="element-3825" /> Abriendo
                      ruta...
                    </> : <>
                      <Package className="w-8 h-8" data-id="element-3826" /> YA RECOGÍ EL PEDIDO
                    </>}
                </button>}
            </div>
          </div>}
      </div>;
  }
  // ─── SCREEN: FULL-SCREEN OFFER (priority) ─────────────────────
  if (priorityOffer) {
    const job = priorityOffer.delivery_jobs;
    const jobData = getJobDisplayData(job);
    return <div className="min-h-screen bg-[#0B0B0B] flex flex-col text-white" data-id="element-3827">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-[#141414] border-b border-white/5" data-id="element-3828">
          <div className="flex items-center gap-2" data-id="element-3829">
            <div className="w-3 h-3 rounded-full bg-[#006847] animate-pulse" data-id="element-3830" />
            <span className="font-bold text-sm text-[#00a870]" data-id="element-3831">
              NUEVA OFERTA
            </span>
          </div>
          <button onClick={handleToggleStatus} className="text-xs text-white/30 font-bold py-1.5 px-3 border border-white/10 rounded-lg" data-id="element-3832">
            Desconectar
          </button>
        </div>

        <div className="flex-1 flex flex-col px-5 py-6" data-id="element-3833">
          {/* Error Banner */}
          {offerError && <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-start gap-3" data-id="element-3834">
              <span className="text-red-400 text-sm font-bold flex-1" data-id="element-3835">
                {offerError}
              </span>
              <button onClick={() => setOfferError(null)} className="text-red-400/60 text-xs font-bold" data-id="element-3836">
                ✕
              </button>
            </div>}

          {/* Payment - Hero */}
          <div className="text-center mb-8" data-id="element-3837">
            <p className="text-6xl font-black text-white mb-1" data-id="element-3838">
              {formatCurrency(jobData.fee, '₡')}
            </p>
            <p className="text-white/40 text-sm" data-id="element-3839">Pago estimado</p>
          </div>

          {/* Countdown */}
          <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 mb-6 flex justify-center" data-id="element-3840">
            <CountdownTimer expiresAt={priorityOffer.expires_at} data-id="element-3841" />
          </div>

          {/* Route */}
          <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 space-y-5 mb-6" data-id="element-3842">
            <div className="flex gap-4 items-start" data-id="element-3843">
              <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center shrink-0 mt-0.5" data-id="element-3844">
                <MapPin className="w-5 h-5 text-white/50" data-id="element-3845" />
              </div>
              <div data-id="element-3846">
                <p className="text-xs text-white/40 uppercase font-bold mb-1" data-id="element-3847">
                  Recoger
                </p>
                <p className="text-base font-bold" data-id="element-3848">{jobData.pickup}</p>
              </div>
            </div>
            <div className="border-t border-white/5" data-id="element-3849" />
            <div className="flex gap-4 items-start" data-id="element-3850">
              <div className="w-10 h-10 rounded-full bg-[#006847]/10 flex items-center justify-center shrink-0 mt-0.5" data-id="element-3851">
                <Navigation className="w-5 h-5 text-[#006847]" data-id="element-3852" />
              </div>
              <div data-id="element-3853">
                <p className="text-xs text-white/40 uppercase font-bold mb-1" data-id="element-3854">
                  Entregar
                </p>
                <p className="text-base font-bold" data-id="element-3855">{jobData.dropoff}</p>
              </div>
            </div>
          </div>

          {/* Remaining offers indicator */}
          {offers.length > 1 && <p className="text-center text-xs text-white/30 mb-4" data-id="element-3856">
              +{offers.length - 1}{' '}
              {offers.length - 1 === 1 ? 'oferta más' : 'ofertas más'} en cola
            </p>}
        </div>

        {/* Action Buttons - Fixed Bottom */}
        <div className="p-5 pb-8 bg-[#141414] border-t border-white/5 flex gap-3" data-id="element-3857">
          <button disabled={actionLoading} onClick={() => handleReject(priorityOffer.id)} className="flex-1 py-6 rounded-2xl bg-[#1A1A1A] border border-white/10 text-white/60 font-black text-xl flex justify-center items-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-3858">
            {actionLoading ? <Loader2 className="w-7 h-7 animate-spin" data-id="element-3859" /> : <>
                <X className="w-7 h-7" data-id="element-3860" /> NO
              </>}
          </button>
          <button disabled={actionLoading} onClick={() => handleAccept(priorityOffer.id)} className="flex-[2] py-6 rounded-2xl bg-[#006847] text-white font-black text-xl flex justify-center items-center gap-3 shadow-2xl shadow-[#006847]/30 active:scale-95 transition-transform disabled:opacity-50" data-id="element-3861">
            {actionLoading ? <Loader2 className="w-7 h-7 animate-spin" data-id="element-3862" /> : <>
                <Check className="w-7 h-7" data-id="element-3863" /> ACEPTAR
              </>}
          </button>
        </div>
      </div>;
  }
  // ─── SCREEN: ONLINE, WAITING FOR OFFERS ───────────────────────
  return <div className="min-h-screen bg-[#0B0B0B] flex flex-col text-white" data-id="element-3864">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4" data-id="element-3865">
        <div className="flex items-center gap-3" data-id="element-3866">
          <div className="w-3 h-3 rounded-full bg-[#006847] animate-pulse" data-id="element-3867" />
          <span className="font-bold text-sm text-[#00a870]" data-id="element-3868">ONLINE</span>
        </div>
        <button onClick={handleLogout} className="p-2 text-white/30" data-id="element-3869">
          <LogOut className="w-5 h-5" data-id="element-3870" />
        </button>
      </div>

      {/* Big online indicator */}
      <div className="flex-1 flex flex-col items-center justify-center px-8" data-id="element-3871">
        <div className="relative mb-8" data-id="element-3872">
          <div className="w-40 h-40 rounded-full bg-[#006847]/5 flex items-center justify-center" data-id="element-3873">
            <div className="w-28 h-28 rounded-full bg-[#006847]/10 flex items-center justify-center animate-pulse" data-id="element-3874">
              <div className="w-20 h-20 rounded-full bg-[#006847]/20 flex items-center justify-center" data-id="element-3875">
                <Wifi className="w-10 h-10 text-[#00a870]" data-id="element-3876" />
              </div>
            </div>
          </div>
          {/* Ping animation */}
          <div className="absolute inset-0 rounded-full border-2 border-[#006847]/20 animate-ping" data-id="element-3877" />
        </div>

        <h2 className="text-2xl font-black text-white mb-2" data-id="element-3878">Buscando viajes</h2>
        <p className="text-white/40 text-center text-sm mb-4" data-id="element-3879">
          Mantén la app abierta para recibir ofertas
        </p>
      </div>

      {/* Debug Panel */}
      {/* Debug panel hidden — uncomment to re-enable:
                                                                  <DriverDebugPanel
                                                                    user={user}
                                                                    driverId={driverId}
                                                                    authDebug={authDebug}
                                                                    offers={offers}
                                                                    debugInfo={debugInfo}
                                                                    isOnline={isOnline}
                                                                    onRefetch={refetch}
                                                                  />
                                                                  */}

      {/* Disconnect button at bottom */}
      <div className="p-5 pb-8" data-id="element-3880">
        <button onClick={handleToggleStatus} disabled={statusToggling} className="w-full py-5 rounded-2xl bg-[#1A1A1A] border border-white/10 text-white/50 font-bold text-lg flex items-center justify-center gap-3 active:scale-95 transition-transform disabled:opacity-50" data-id="element-3881">
          {statusToggling ? <Loader2 className="w-6 h-6 animate-spin" data-id="element-3882" /> : <>
              <Power className="w-6 h-6" data-id="element-3883" />
              DESCONECTAR
            </>}
        </button>
      </div>
    </div>;
}